import type {
    IFormFieldValue,
    IResponseTiming,
    IResponseTimingInput,
} from "@agrigate/survey-api-def/lib/SurveysService/dto";
import type { SurveyGraphLike } from "@agrigate/survey-api-def/lib/SurveysService/graph";
import {
    buildGraphIndex,
    resolveNextQuestion,
    shortestRemainingSteps,
    walkAnsweredPath,
} from "@agrigate/survey-api-def/lib/SurveysService/graph";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import {
    type ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import type { SurveyQuestion } from "../SurveyBuilderForm";
import SurveyQuestionGenerator from "../SurveyQuestionGenerator";
import { isAnswered } from "./answers";
import useQuestionTiming from "./useQuestionTiming";

export interface GraphMultiStepFormHook {
    step: ReactElement | undefined;
    isFirstStep: boolean;
    isLastStep: boolean;
    next: () => void;
    back: () => void;
    answers: Record<string, string | string[]>;
    doneCount: number;
    estimatedTotal: number;
    progressValue: number;
    isCurrentQuestionRequired: boolean;
    isCurrentQuestionAnswered: boolean;
    canAdvance: boolean;
    readTiming: () => IResponseTimingInput;
    path: string[];
}

const useGraphMultiStepForm = (
    config: SurveyGraphLike & { questions: SurveyQuestion[] },
    initialResponses?: MaybeN<IFormFieldValue[]>,
    initialTiming?: MaybeN<IResponseTiming>,
): GraphMultiStepFormHook => {
    const index = useMemo(() => buildGraphIndex(config), [config]);
    const questionById = useMemo(() => {
        const byId: Record<string, SurveyQuestion> = {};
        for (const question of config.questions) byId[question.id] = question;
        return byId;
    }, [config]);

    const [stack, setStack] = useState<string[]>([]);
    const [cursor, setCursor] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string | string[]>>(
        {},
    );
    const hasHydratedRef = useRef(false);
    const maxProgressRef = useRef(0);

    const currentQuestionId = stack[cursor];
    const { readTiming } = useQuestionTiming(currentQuestionId, initialTiming);

    const initialAnswers = useMemo(() => {
        const restored: Record<string, string | string[]> = {};
        for (const response of initialResponses ?? []) {
            if (response.id && response.value != null)
                restored[response.id] = response.value;
            for (const levelOne of response.levelOneResponses ?? []) {
                if (levelOne.id && levelOne.value != null)
                    restored[levelOne.id] = levelOne.value;
            }
        }
        return restored;
    }, [initialResponses]);

    useEffect(() => {
        if (hasHydratedRef.current) return;
        if (!initialResponses) return;
        hasHydratedRef.current = true;

        setAnswers(initialAnswers);
        const walk = walkAnsweredPath(index, id => initialAnswers[id]);
        const restored = walk.visited.length
            ? walk.visited
            : index.entryId
              ? [index.entryId]
              : [];
        setStack(restored);
        const hint = initialTiming?.lastQuestionId;
        const hintIdx = hint ? restored.indexOf(hint) : -1;
        setCursor(hintIdx >= 0 ? hintIdx : Math.max(0, restored.length - 1));
    }, [initialAnswers, initialResponses, index, initialTiming]);

    useEffect(() => {
        if (stack.length || !index.entryId) return;
        setStack([index.entryId]);
        setCursor(0);
    }, [index, stack.length]);

    const onAnswerChange = useCallback(
        (questionId: string, value: string | string[]) => {
            setAnswers(prev => ({ ...prev, [questionId]: value }));
            const next = resolveNextQuestion(index, questionId, value);
            const nextId = next.kind === "end" ? undefined : next.questionId;
            setStack(prev =>
                prev[cursor + 1] === nextId ? prev : prev.slice(0, cursor + 1),
            );
        },
        [index, cursor],
    );

    const step = useMemo(() => {
        const question = currentQuestionId
            ? questionById[currentQuestionId]
            : undefined;
        if (!question) return undefined;
        return (
            <SurveyQuestionGenerator
                key={question.id}
                surveyQuestion={question}
                onChange={value => onAnswerChange(question.id, value)}
                value={answers[question.id]}
            />
        );
    }, [currentQuestionId, questionById, answers, onAnswerChange]);

    const nextTarget = useMemo(
        () =>
            currentQuestionId
                ? resolveNextQuestion(
                      index,
                      currentQuestionId,
                      answers[currentQuestionId],
                  )
                : null,
        [index, currentQuestionId, answers],
    );

    const nextQuestionId =
        nextTarget?.kind === "question" && questionById[nextTarget.questionId]
            ? nextTarget.questionId
            : null;

    const isUndecided =
        nextTarget?.kind === "end" &&
        nextTarget.via === "no-link" &&
        !!currentQuestionId &&
        !isAnswered(answers[currentQuestionId]) &&
        !!index.optionLinks.get(currentQuestionId)?.length;

    const isLastStep = !nextQuestionId && !isUndecided;

    const next = useCallback(() => {
        if (!currentQuestionId || !nextQuestionId) return;
        const nextId = nextQuestionId;
        setStack(prev =>
            prev[cursor + 1] === nextId
                ? prev
                : [...prev.slice(0, cursor + 1), nextId],
        );
        setCursor(c => c + 1);
    }, [currentQuestionId, nextQuestionId, cursor]);

    const back = useCallback(() => setCursor(c => (c <= 0 ? c : c - 1)), []);

    const currentQuestion = currentQuestionId
        ? questionById[currentQuestionId]
        : undefined;
    const isCurrentQuestionRequired = currentQuestion?.isRequired ?? false;
    const isCurrentQuestionAnswered = currentQuestionId
        ? isAnswered(answers[currentQuestionId])
        : true;
    const canAdvance = !(
        (isCurrentQuestionRequired || isUndecided) &&
        !isCurrentQuestionAnswered
    );

    const doneCount = cursor + 1;
    const remaining = currentQuestionId
        ? shortestRemainingSteps(index, currentQuestionId)
        : 1;
    const estimatedTotal = Math.max(doneCount, doneCount + remaining - 1);
    const rawProgress = (doneCount / Math.max(1, estimatedTotal)) * 100;
    maxProgressRef.current = Math.max(maxProgressRef.current, rawProgress);
    const progressValue = isLastStep ? 100 : maxProgressRef.current;

    const path = useMemo(
        () => walkAnsweredPath(index, id => answers[id]).visited,
        [index, answers],
    );

    return {
        step,
        isFirstStep: cursor === 0,
        isLastStep,
        next,
        back,
        answers,
        doneCount,
        estimatedTotal,
        progressValue,
        isCurrentQuestionRequired,
        isCurrentQuestionAnswered,
        canAdvance,
        readTiming,
        path,
    };
};

export default useGraphMultiStepForm;
